import { useCallback, useContext, useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { VecvizChartConfig } from "./VecVizConfig";
import { includeAndExcludeVectorSets } from "../../../utils/dataFunctions";
import { config as VecVizConfig } from "../../../d3charts/vecviz/config";
import { applyOverrides, getFilteredData } from "../../../utils/dataFunctions";
import { StoreContext } from "../../../store/Store";
import { LocalStoreContext } from "../store/LocalStore";
import { RefContext } from "../store/RefContext";
import { actionTypes as lactionTypes } from "../store/storeTypes";
import { actionTypes } from "../../../store/storeTypes";
import { config as combinedConfig } from "./combinedConfig";
import { Loader } from "../../../components/loader/Loader";

const parseDate = d3.timeParse("%m/%d/%Y");
const formatTime = d3.timeFormat("%m/%d/%Y");

export const CombinedChart = () => {
  const [store, dispatch] = useContext(StoreContext);
  const [lstore, ldispatch] = useContext(LocalStoreContext);
  const refs = useContext(RefContext);
  const headerRef = useRef(null);
  const { activeLineName, filter, modelDateItem, strengthData, vectorSet2 } =
    lstore;

  const [historyData, setHistoryData] = useState();
  const [historyConfig, setHistoryConfig] = useState();
  const [strengthConfig, setStrengthConfig] = useState();
  const [pxRange, setPxRange] = useState();
  const [forceRefreshCombo, setForceRefreshCombo] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [ready, setReady] = useState(false);
  if (store) {
    //do nothing
  }

  const setTooltip = useCallback(
    (tooltip) => {
      dispatch({ type: actionTypes.SET_TOOLTIP, payload: tooltip });
    },
    [dispatch]
  );
  const setForceRefresh = useCallback(() => {
    setForceRefreshCombo(Math.random());
  }, []);
  const setBrushRefCombo = useCallback(
    (ext, axis) => {
      refs.layoutRefs.current["brush"]["History"][axis] = ext;
    },
    [refs.layoutRefs]
  );
  const setActiveLine = useCallback(
    (name) => {
      ldispatch({ type: lactionTypes.SET_ACTIVELINE, payload: name });
    },
    [ldispatch]
  );
  useEffect(() => {
    if (headerRef.current && ready) {
      setHeaderHeight(headerRef.current.clientHeight + 1);
    }
  }, [headerRef, ready]);

  // --- Auto-Refresh Fix ---
  useEffect(() => {
    if (ready) {
      const timer = setTimeout(() => {
        setForceRefreshCombo(Math.random());
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [ready]);
  // ------------------------

  useEffect(() => {
    refs.layoutRefs.current["brush"]["History"] = {
      x: [],
      y: [],
    };
  }, [refs.layoutRefs]);
  useEffect(() => {
    if (vectorSet2.rawdata.length > 0 && modelDateItem) {
      const modelDate = formatTime(modelDateItem.date);
      const modelDateValue = modelDateItem.value;
      const firstForecastDate =
        vectorSet2.rawdatacombined["Sigma_95Frcst"]["Sigma_95D"][0].Date;

      const historyConfig = applyOverrides(VecVizConfig, {
        activeLine: { active: true, respond: true },
        brush: { y: { active: true } },
        ignoreVectorsInVectorSetFilter: true,

        layout: { margin: { left: 50, bottom: 35, right: 0 } },
        axes: {
          y: { tickSizeInner: 5 },
          x: { tickSizeInner: 20, tickLineColour: "none" },
        },
        header: {
          text: `${refs.vectorset2CurrentTicker.current} Px & Vector History`,
        },

        valueFilter: (d) =>
          parseDate(d.Date) <= parseDate(firstForecastDate),
        horizontalLines: {
          [modelDateValue]: {
            line: { stroke: "black", strokeWidth: 1 },
            label: {
              textAnchor: "start",
              colour: "grey",

              fontSize: 10,
              fontWeight: 400,
              dy: "-0.5em",
              dx: "0.5em",
            },
          },
        },
      });
      setHistoryConfig(historyConfig);
      const strengthConfig = applyOverrides(VecVizConfig, {
        activeLine: { active: true, respond: true },
        brush: { y: { active: true, width: 0 } },
        header: {
          text: `${refs.vectorset2CurrentTicker.current} Vector Strength Histogram`,
        },
        ignoreVectorsInVectorSetFilter: true,
        valueFilter: (d) => parseDate(d.Date) >= parseDate(modelDate),
        axes: {
          y: { show: false, tickSizeInner: 0 },
          x: { tickSizeInner: 20, tickLineColour: "none" },
        },
        layout: {
          margin: {
            left: 0,
            bottom: 35,
          },
        },
        verticalLines: {
          [modelDate]: {
            line: { stroke: "black", strokeWidth: 3, extendBelow: 20 },
            label: {
              textAnchor: "start",
              colour: "grey",
              fontSize: 10,
              fontWeight: 400,
              dy: "1em",
              dx: "0.5em",
            },
          },
        },
        horizontalLines: {
          [modelDateValue]: {
            line: { stroke: "black", strokeWidth: 1 },
          },
        },
      });
      setStrengthConfig(strengthConfig);
      const historyData = getFilteredData(
        vectorSet2.rawdatacombined,
        {
          name: activeLineName,
          style: historyConfig.activeLine.style,
          backgroundStyle: historyConfig.activeLine.backgroundStyle,
          respond: historyConfig.activeLine.respond,
        },
        includeAndExcludeVectorSets(
          { ...filter, VectorSet: [] },

          ["Close", "TopBottom"],
          [],
          vectorSet2.rawdata,
          (item) => item.VectorSet.slice(0, 2) === "vs"
        ),
        historyConfig.colourMapping,
        historyConfig.valueFilter,
        historyConfig.ignoreVectorsInVectorSetFilter
      );
      setHistoryData(historyData);

      const strengthDataNew = getFilteredData(
        vectorSet2.rawdatacombined,
        {
          name: activeLineName,
          style: strengthConfig.activeLine.style,
          backgroundStyle: strengthConfig.activeLine.backgroundStyle,
          respond: strengthConfig.activeLine.respond,
        },
        includeAndExcludeVectorSets(
          { ...filter, VectorSet: [] },

          [
            "Sigma_95Frcst",
            "Vector_95Frcst",
            "Sigma_99Frcst",
            "Vector_99Frcst",
            "Vector_BodyFrcst",
          ],
          ["Close", "TopBottom"],
          vectorSet2.rawdata,

          (item) => item.VectorSet.slice(0, 2) === "vs"
        ),
        strengthConfig.colourMapping,
        strengthConfig.valueFilter,
        strengthConfig.ignoreVectorsInVectorSetFilter
      );
      ldispatch({
        type: lactionTypes.SET_STRENGTH_DATA,
        payload: strengthDataNew,
      });
      const combinedData = strengthDataNew.concat(historyData);
      
      // --- START PERFORMANCE OPTIMIZATION ---
      let dataMin = Infinity;
      let dataMax = -Infinity;

      combinedData.forEach((series) => {
        if (series.values) {
          series.values.forEach((point) => {
            if (point.px < dataMin) dataMin = point.px;
            if (point.px > dataMax) dataMax = point.px;
          });
        }
      });

      if (dataMin === Infinity) dataMin = 0;
      if (dataMax === -Infinity) dataMax = 100;

      setPxRange({ min: dataMin, max: dataMax });
      // --- END PERFORMANCE OPTIMIZATION ---

      setReady(true);
    }
  }, [
    vectorSet2.rawdatacombined,
    vectorSet2.rawdata,
    filter,
    activeLineName,
    ldispatch,
    refs.layoutRefs,
    refs.vectorset2CurrentTicker,
    modelDateItem,
  ]);
  return ready ? (
    <div
      style={{
        display: "block",
        width: refs.componentSize.current["CombinedChart"].width,
        height: refs.componentSize.current["CombinedChart"].height,
      }}
    >
      <div
        ref={headerRef}
        style={{
          // height: combinedConfig.headerHeight,
          display: "flex",

          justifyContent: "flex-start",
          fontSize: combinedConfig.fontSize,
          fontWeight: combinedConfig.fontWeight,
          borderBottom: combinedConfig.borderBottom,
        }}
      >
        {combinedConfig.headerText}
      </div>
      <div
        ref={refs.comboRef}
        style={{
          display: "flex",
        }}
      >
        <div style={{ width: "50%" }}>
          <VecvizChartConfig
            data={historyData}
            pxRange={pxRange}
            width={refs.componentSize.current["CombinedChart"].width / 2}
            height={
              refs.componentSize.current["CombinedChart"].height - headerHeight
            }
            setActiveLine={setActiveLine}
            brushRef={refs.layoutRefs.current["brush"]["History"]}
            setBrushRef={setBrushRefCombo}
            setTooltip={setTooltip}
            config={historyConfig}
            setForceRefresh={setForceRefresh}
            forceRefresh={undefined}
            lRef={refs.historyRef}
          />
        </div>
        <div style={{ width: "50%" }}>
          <VecvizChartConfig
            data={strengthData}
            pxRange={pxRange}
            width={refs.componentSize.current["CombinedChart"].width / 2}
            height={
              refs.componentSize.current["CombinedChart"].height - headerHeight
            }
            setActiveLine={setActiveLine}
            brushRef={refs.layoutRefs.current["brush"]["History"]}
            setBrushRef={undefined}
            setTooltip={setTooltip}
            config={strengthConfig}
            setForceRefresh={undefined}
            forceRefresh={forceRefreshCombo}
            lRef={refs.strengthRef}
          />
        </div>
      </div>
    </div>
  ) : (
    <div
      style={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column", // Stack loader and text
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Loader />
      {/* WARNING TEXT ONLY ON LOADING SCREEN */}
      <div style={{ marginTop: "10px", color: "grey", fontSize: "12px" }}>
        Initial load is slow, subsequent loads are fast...
      </div>
    </div>
  );
};